/**
 * Email Tracking Service - Optimized Implementation
 *
 * Handles email tracking operations including:
 * - Logging sent emails
 * - Fetching user email statistics
 * - Platform-wide email analytics
 * - Recent email events
 *
 * Optimization improvements (January 20, 2026):
 * - Added withRetry for all operations
 * - Added input validation (userId, email, dates)
 * - Added correlation IDs for request tracing
 * - Added structured logging throughout
 * - Added comprehensive TypeScript interfaces
 * - Added rate limit awareness
 */

import { supabase } from './supabaseClient';
import { logger } from '../lib/logger';
import { withRetry, createAppError, DEFAULT_RETRY_CONFIG, type AppError } from './errorHandler';

/**
 * Helper to perform typed select on untyped tables.
 * Used for tables/views (like email_analytics) not in generated Supabase types.
 */
async function selectFromUntyped<T>(
    table: string,
    selectColumns: string,
    filters?: { gte?: [string, string]; lte?: [string, string] },
    orderBy?: { column: string; ascending: boolean }
): Promise<{ data: T[] | null; error: { message: string } | null }> {
    const fromFn = supabase.from.bind(supabase) as unknown as (
        relation: string
    ) => { select: (columns: string) => Record<string, unknown> };
    let query = fromFn(table).select(selectColumns);

    if (filters?.gte) {
        query = (query as unknown as { gte: (col: string, val: string) => Record<string, unknown> }).gte(filters.gte[0], filters.gte[1]);
    }
    if (filters?.lte) {
        query = (query as unknown as { lte: (col: string, val: string) => Record<string, unknown> }).lte(filters.lte[0], filters.lte[1]);
    }
    if (orderBy) {
        query = (query as unknown as { order: (col: string, opts: { ascending: boolean }) => Record<string, unknown> }).order(orderBy.column, { ascending: orderBy.ascending });
    }

    return query as unknown as Promise<{ data: T[] | null; error: { message: string } | null }>;
}

// ============================================
// CONSTANTS
// ============================================

/** UUID v4 validation regex */
const UUID_V4_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** RFC 5322 email validation regex */
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/** Maximum days to look back for analytics */
const MAX_ANALYTICS_DAYS = 365;

/** Valid email types */
const VALID_EMAIL_TYPES = ['confirmation', 'reset', 'magic_link', 'invite', 'notification'] as const;

// ============================================
// TYPES
// ============================================

export type EmailType = typeof VALID_EMAIL_TYPES[number];

export interface EmailLogData {
    emailId: string;
    userId?: string;
    recipient: string;
    subject: string;
    fromAddress: string;
    emailType: EmailType;
}

export interface EmailStats {
    total: number;
    delivered: number;
    opened: number;
    clicked: number;
    bounced: number;
    emails: EmailLogRecord[];
}

export interface EmailLogRecord {
    id: string;
    email_id: string;
    user_id: string | null;
    recipient: string;
    subject: string;
    from_address: string;
    email_type: EmailType;
    sent: boolean;
    sent_at: string;
    delivered: boolean;
    delivered_at: string | null;
    opened: boolean;
    opened_at: string | null;
    clicked: boolean;
    clicked_at: string | null;
    bounced: boolean;
    bounced_at: string | null;
    created_at: string;
}

export interface EmailAnalyticsRow {
    date: string;
    email_type?: string;
    total_sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    bounced: number;
    delivery_rate: number;
    open_rate: number;
    click_rate: number;
}

export interface EmailEvent {
    id: string;
    email_log_id: string;
    event_type: 'delivered' | 'opened' | 'clicked' | 'bounced' | 'complained' | 'unsubscribed';
    event_data: Record<string, unknown>;
    created_at: string;
}

export interface EmailTrackingResult<T> {
    success: boolean;
    data?: T;
    error?: AppError;
    correlationId: string;
}

// ============================================
// VALIDATION HELPERS
// ============================================

/**
 * Validate UUID v4 format
 */
export const isValidUUID = (uuid: string): boolean => {
    return UUID_V4_REGEX.test(uuid);
};

/**
 * Validate email format (RFC 5322)
 */
export const isValidEmail = (email: string): boolean => {
    if (!email || typeof email !== 'string') return false;
    const trimmed = email.trim();
    return trimmed.length > 0 && trimmed.length <= 254 && EMAIL_REGEX.test(trimmed);
};

/**
 * Validate email type
 */
export const isValidEmailType = (type: string): type is EmailType => {
    return VALID_EMAIL_TYPES.includes(type as EmailType);
};

/**
 * Validate date range for analytics
 */
const validateDateRange = (startDate: Date, endDate: Date): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (!(startDate instanceof Date) || isNaN(startDate.getTime())) {
        errors.push('Invalid start date');
    }

    if (!(endDate instanceof Date) || isNaN(endDate.getTime())) {
        errors.push('Invalid end date');
    }

    if (errors.length === 0) {
        if (startDate > endDate) {
            errors.push('Start date must be before end date');
        }

        const diffDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        if (diffDays > MAX_ANALYTICS_DAYS) {
            errors.push(`Date range cannot exceed ${MAX_ANALYTICS_DAYS} days`);
        }

        // Don't allow future dates
        const now = new Date();
        if (endDate > now) {
            errors.push('End date cannot be in the future');
        }
    }

    return { valid: errors.length === 0, errors };
};

/**
 * Validate email log data
 */
const validateEmailLogData = (data: EmailLogData): void => {
    const errors: string[] = [];

    if (!data.emailId || typeof data.emailId !== 'string' || data.emailId.trim().length === 0) {
        errors.push('Email ID is required');
    }

    if (data.userId && !isValidUUID(data.userId)) {
        errors.push('Invalid user ID format');
    }

    if (!isValidEmail(data.recipient)) {
        errors.push('Invalid recipient email address');
    }

    if (!data.subject || typeof data.subject !== 'string' || data.subject.trim().length === 0) {
        errors.push('Subject is required');
    }

    if (!isValidEmail(data.fromAddress)) {
        errors.push('Invalid from address');
    }

    if (!isValidEmailType(data.emailType)) {
        errors.push(`Invalid email type. Must be one of: ${VALID_EMAIL_TYPES.join(', ')}`);
    }

    if (errors.length > 0) {
        throw createAppError(
            { message: errors.join('; '), code: 'VALIDATION_ERROR' },
            'emailTrackingService.validateEmailLogData'
        );
    }
};

/**
 * Generate a correlation ID for request tracing
 */
const generateCorrelationId = (): string => {
    return crypto.randomUUID();
};

/**
 * Mask email for logging (privacy)
 */
const maskEmail = (email: string): string => {
    return email.replace(/(.{2}).+(@.+)/, '$1***$2');
};

// ============================================
// EMAIL TRACKING SERVICE
// ============================================

export const emailTrackingService = {
    /**
     * Log an email when it's sent
     *
     * Includes:
     * - Input validation (emailId, recipient, subject, fromAddress, emailType)
     * - Retry logic with exponential backoff
     * - Correlation ID for request tracing
     */
    logEmailSent: async (data: EmailLogData): Promise<EmailTrackingResult<{ id: string }>> => {
        const correlationId = generateCorrelationId();

        logger.info('Logging email sent', {
            correlationId,
            emailId: data.emailId,
            emailType: data.emailType,
            recipient: maskEmail(data.recipient),
        });

        try {
            // Validate input
            validateEmailLogData(data);

            const result = await withRetry(
                async () => {
                    const { data: insertedData, error } = await supabase
                        .from('email_logs')
                        .insert({
                            email_id: data.emailId,
                            user_id: data.userId,
                            recipient: data.recipient.trim().toLowerCase(),
                            subject: data.subject.trim(),
                            from_address: data.fromAddress.trim().toLowerCase(),
                            email_type: data.emailType,
                            sent: true,
                            sent_at: new Date().toISOString(),
                        })
                        .select('id')
                        .single();

                    if (error) {
                        throw error;
                    }

                    return insertedData;
                },
                'emailTrackingService.logEmailSent',
                DEFAULT_RETRY_CONFIG
            );

            logger.info('Email logged successfully', {
                correlationId,
                emailId: data.emailId,
                logId: result.id,
            });

            return { success: true, data: result, correlationId };
        } catch (error) {
            const appError = createAppError(error, 'emailTrackingService.logEmailSent');
            logger.error('Failed to log email', error instanceof Error ? error : undefined, {
                correlationId,
                emailId: data.emailId,
                category: appError.category,
            });
            return { success: false, error: appError, correlationId };
        }
    },

    /**
     * Get email statistics for a user
     *
     * Includes:
     * - Input validation (userId)
     * - Retry logic with exponential backoff
     * - Correlation ID for request tracing
     */
    getUserEmailStats: async (userId: string): Promise<EmailStats> => {
        const correlationId = generateCorrelationId();

        logger.debug('Fetching user email stats', {
            correlationId,
            userId,
        });

        // Validate input
        if (!userId || !isValidUUID(userId)) {
            throw createAppError(
                { message: 'Invalid user ID format', code: 'VALIDATION_ERROR' },
                'emailTrackingService.getUserEmailStats'
            );
        }

        try {
            return await withRetry(
                async () => {
                    const { data, error } = await supabase
                        .from('email_logs')
                        .select('*')
                        .eq('user_id', userId)
                        .order('sent_at', { ascending: false });

                    if (error) {
                        throw error;
                    }

                    // Handle null/undefined data gracefully
                    const emails = (data || []) as unknown as EmailLogRecord[];

                    const stats: EmailStats = {
                        total: emails.length,
                        delivered: emails.filter(e => e?.delivered === true).length,
                        opened: emails.filter(e => e?.opened === true).length,
                        clicked: emails.filter(e => e?.clicked === true).length,
                        bounced: emails.filter(e => e?.bounced === true).length,
                        emails,
                    };

                    logger.debug('User email stats fetched', {
                        correlationId,
                        userId,
                        total: stats.total,
                    });

                    return stats;
                },
                'emailTrackingService.getUserEmailStats',
                DEFAULT_RETRY_CONFIG
            );
        } catch (error) {
            // Re-throw AppErrors as-is, wrap others
            if ((error as AppError).category) {
                throw error;
            }
            const appError = createAppError(error, 'emailTrackingService.getUserEmailStats');
            logger.error('Failed to fetch user email stats', error instanceof Error ? error : undefined, {
                correlationId,
                userId,
                category: appError.category,
            });
            throw appError;
        }
    },

    /**
     * Get platform-wide email analytics
     *
     * Includes:
     * - Input validation (date range)
     * - Retry logic with exponential backoff
     * - Correlation ID for request tracing
     * - Rate normalization (0-100 range)
     */
    getEmailAnalytics: async (startDate: Date, endDate: Date): Promise<EmailAnalyticsRow[]> => {
        const correlationId = generateCorrelationId();

        // Validate date range
        const dateValidation = validateDateRange(startDate, endDate);
        if (!dateValidation.valid) {
            throw createAppError(
                { message: dateValidation.errors.join('; '), code: 'VALIDATION_ERROR' },
                'emailTrackingService.getEmailAnalytics'
            );
        }

        const startDateStr = startDate.toISOString().split('T')[0];
        const endDateStr = endDate.toISOString().split('T')[0];

        logger.debug('Fetching email analytics', {
            correlationId,
            startDate: startDateStr,
            endDate: endDateStr,
        });

        try {
            return await withRetry(
                async () => {
                    const { data, error } = await selectFromUntyped<EmailAnalyticsRow>(
                        'email_analytics',
                        '*',
                        { gte: ['date', startDateStr], lte: ['date', endDateStr] },
                        { column: 'date', ascending: false }
                    );

                    if (error) {
                        throw error;
                    }

                    // Handle null/undefined data and normalize rate values
                    const normalizedData: EmailAnalyticsRow[] = (data || []).map(row => ({
                        date: row?.date ?? '',
                        email_type: row?.email_type ?? undefined,
                        // Ensure counts are valid numbers
                        total_sent: Math.max(0, row?.total_sent ?? 0),
                        delivered: Math.max(0, row?.delivered ?? 0),
                        opened: Math.max(0, row?.opened ?? 0),
                        clicked: Math.max(0, row?.clicked ?? 0),
                        bounced: Math.max(0, row?.bounced ?? 0),
                        // Clamp percentage rates to 0-100 range
                        delivery_rate: Math.min(100, Math.max(0, row?.delivery_rate ?? 0)),
                        open_rate: Math.min(100, Math.max(0, row?.open_rate ?? 0)),
                        click_rate: Math.min(100, Math.max(0, row?.click_rate ?? 0)),
                    }));

                    logger.debug('Email analytics fetched', {
                        correlationId,
                        rowCount: normalizedData.length,
                    });

                    return normalizedData;
                },
                'emailTrackingService.getEmailAnalytics',
                DEFAULT_RETRY_CONFIG
            );
        } catch (error) {
            // Re-throw AppErrors as-is, wrap others
            if ((error as AppError).category) {
                throw error;
            }
            const appError = createAppError(error, 'emailTrackingService.getEmailAnalytics');
            logger.error('Failed to fetch email analytics', error instanceof Error ? error : undefined, {
                correlationId,
                startDate: startDateStr,
                endDate: endDateStr,
                category: appError.category,
            });
            throw appError;
        }
    },

    /**
     * Get recent email events
     *
     * Includes:
     * - Input validation (limit)
     * - Retry logic with exponential backoff
     * - Correlation ID for request tracing
     */
    getRecentEvents: async (limit = 100): Promise<EmailEvent[]> => {
        const correlationId = generateCorrelationId();

        // Validate limit
        if (typeof limit !== 'number' || limit < 1 || limit > 1000) {
            throw createAppError(
                { message: 'Limit must be a number between 1 and 1000', code: 'VALIDATION_ERROR' },
                'emailTrackingService.getRecentEvents'
            );
        }

        logger.debug('Fetching recent email events', {
            correlationId,
            limit,
        });

        try {
            return await withRetry(
                async () => {
                    const { data, error } = await supabase
                        .from('email_events')
                        .select('*')
                        .order('created_at', { ascending: false })
                        .limit(limit);

                    if (error) {
                        throw error;
                    }

                    const events = (data || []) as unknown as EmailEvent[];

                    logger.debug('Recent email events fetched', {
                        correlationId,
                        count: events.length,
                    });

                    return events;
                },
                'emailTrackingService.getRecentEvents',
                DEFAULT_RETRY_CONFIG
            );
        } catch (error) {
            // Re-throw AppErrors as-is, wrap others
            if ((error as AppError).category) {
                throw error;
            }
            const appError = createAppError(error, 'emailTrackingService.getRecentEvents');
            logger.error('Failed to fetch recent email events', error instanceof Error ? error : undefined, {
                correlationId,
                limit,
                category: appError.category,
            });
            throw appError;
        }
    },

    /**
     * Get email statistics summary (platform-wide)
     *
     * Includes:
     * - Retry logic with exponential backoff
     * - Correlation ID for request tracing
     */
    getEmailStatsSummary: async (): Promise<{
        totalSent: number;
        deliveryRate: number;
        openRate: number;
        clickRate: number;
        bounceRate: number;
    }> => {
        const correlationId = generateCorrelationId();

        logger.debug('Fetching email stats summary', { correlationId });

        try {
            return await withRetry(
                async () => {
                    // Get last 30 days of analytics
                    const endDate = new Date();
                    const startDate = new Date();
                    startDate.setDate(startDate.getDate() - 30);

                    const { data, error } = await selectFromUntyped<Pick<EmailAnalyticsRow, 'total_sent' | 'delivered' | 'opened' | 'clicked' | 'bounced'>>(
                        'email_analytics',
                        'total_sent, delivered, opened, clicked, bounced',
                        {
                            gte: ['date', startDate.toISOString().split('T')[0]],
                            lte: ['date', endDate.toISOString().split('T')[0]],
                        }
                    );

                    if (error) {
                        throw error;
                    }

                    const rows = data || [];

                    // Aggregate totals
                    const totals = rows.reduce(
                        (acc, row) => ({
                            totalSent: acc.totalSent + (row?.total_sent ?? 0),
                            delivered: acc.delivered + (row?.delivered ?? 0),
                            opened: acc.opened + (row?.opened ?? 0),
                            clicked: acc.clicked + (row?.clicked ?? 0),
                            bounced: acc.bounced + (row?.bounced ?? 0),
                        }),
                        { totalSent: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0 }
                    );

                    // Calculate rates (avoid division by zero)
                    const deliveryRate = totals.totalSent > 0
                        ? Math.round((totals.delivered / totals.totalSent) * 100)
                        : 0;
                    const openRate = totals.delivered > 0
                        ? Math.round((totals.opened / totals.delivered) * 100)
                        : 0;
                    const clickRate = totals.opened > 0
                        ? Math.round((totals.clicked / totals.opened) * 100)
                        : 0;
                    const bounceRate = totals.totalSent > 0
                        ? Math.round((totals.bounced / totals.totalSent) * 100)
                        : 0;

                    const summary = {
                        totalSent: totals.totalSent,
                        deliveryRate,
                        openRate,
                        clickRate,
                        bounceRate,
                    };

                    logger.debug('Email stats summary fetched', {
                        correlationId,
                        ...summary,
                    });

                    return summary;
                },
                'emailTrackingService.getEmailStatsSummary',
                DEFAULT_RETRY_CONFIG
            );
        } catch (error) {
            const appError = createAppError(error, 'emailTrackingService.getEmailStatsSummary');
            logger.error('Failed to fetch email stats summary', error instanceof Error ? error : undefined, {
                correlationId,
                category: appError.category,
            });
            throw appError;
        }
    },

    /**
     * Get emails by type
     *
     * Includes:
     * - Input validation (emailType, limit)
     * - Retry logic with exponential backoff
     * - Correlation ID for request tracing
     */
    getEmailsByType: async (emailType: EmailType, limit = 100): Promise<EmailLogRecord[]> => {
        const correlationId = generateCorrelationId();

        // Validate input
        if (!isValidEmailType(emailType)) {
            throw createAppError(
                { message: `Invalid email type. Must be one of: ${VALID_EMAIL_TYPES.join(', ')}`, code: 'VALIDATION_ERROR' },
                'emailTrackingService.getEmailsByType'
            );
        }

        if (typeof limit !== 'number' || limit < 1 || limit > 1000) {
            throw createAppError(
                { message: 'Limit must be a number between 1 and 1000', code: 'VALIDATION_ERROR' },
                'emailTrackingService.getEmailsByType'
            );
        }

        logger.debug('Fetching emails by type', {
            correlationId,
            emailType,
            limit,
        });

        try {
            return await withRetry(
                async () => {
                    const { data, error } = await supabase
                        .from('email_logs')
                        .select('*')
                        .eq('email_type', emailType)
                        .order('sent_at', { ascending: false })
                        .limit(limit);

                    if (error) {
                        throw error;
                    }

                    const emails = (data || []) as unknown as EmailLogRecord[];

                    logger.debug('Emails by type fetched', {
                        correlationId,
                        emailType,
                        count: emails.length,
                    });

                    return emails;
                },
                'emailTrackingService.getEmailsByType',
                DEFAULT_RETRY_CONFIG
            );
        } catch (error) {
            const appError = createAppError(error, 'emailTrackingService.getEmailsByType');
            logger.error('Failed to fetch emails by type', error instanceof Error ? error : undefined, {
                correlationId,
                emailType,
                category: appError.category,
            });
            throw appError;
        }
    },

    /**
     * Search emails by recipient
     *
     * Includes:
     * - Input validation (email, limit)
     * - Retry logic with exponential backoff
     * - Correlation ID for request tracing
     */
    searchByRecipient: async (email: string, limit = 100): Promise<EmailLogRecord[]> => {
        const correlationId = generateCorrelationId();

        // Validate input - partial email is OK for search
        if (!email || typeof email !== 'string' || email.trim().length < 3) {
            throw createAppError(
                { message: 'Search term must be at least 3 characters', code: 'VALIDATION_ERROR' },
                'emailTrackingService.searchByRecipient'
            );
        }

        if (typeof limit !== 'number' || limit < 1 || limit > 1000) {
            throw createAppError(
                { message: 'Limit must be a number between 1 and 1000', code: 'VALIDATION_ERROR' },
                'emailTrackingService.searchByRecipient'
            );
        }

        logger.debug('Searching emails by recipient', {
            correlationId,
            searchTerm: maskEmail(email),
            limit,
        });

        try {
            return await withRetry(
                async () => {
                    const { data, error } = await supabase
                        .from('email_logs')
                        .select('*')
                        .ilike('recipient', `%${email.trim().toLowerCase()}%`)
                        .order('sent_at', { ascending: false })
                        .limit(limit);

                    if (error) {
                        throw error;
                    }

                    const emails = (data || []) as unknown as EmailLogRecord[];

                    logger.debug('Emails by recipient found', {
                        correlationId,
                        count: emails.length,
                    });

                    return emails;
                },
                'emailTrackingService.searchByRecipient',
                DEFAULT_RETRY_CONFIG
            );
        } catch (error) {
            const appError = createAppError(error, 'emailTrackingService.searchByRecipient');
            logger.error('Failed to search emails by recipient', error instanceof Error ? error : undefined, {
                correlationId,
                category: appError.category,
            });
            throw appError;
        }
    },
};

// Export validation utilities
export { maskEmail };
